import { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ArrowLeft, Plus, Pencil, Trash2, FileText, ShoppingCart, Receipt, RotateCcw, UserCheck, Mail, Sparkles, Loader2, Info, PackageCheck, Save, X, CreditCard, Check, Search, MapPin, Phone, MoreHorizontal, CheckSquare, Archive } from "lucide-react";
import { clientAPI, productContractAPI, aiAPI, adminAPI } from "@/lib/api";
import ContractForm from "@/components/ContractForm";
import { ContractSummaryCell } from "@/components/ContractSummaryCell";
import { usePaymentMethods } from "@/lib/paymentMethods";
import { legalIdField, legalIdFields } from "@/lib/legalIdentity";

const PAYMENT_METHOD_LABELS = { virement: "Virement", cheque: "Chèque", carte: "Carte bancaire", "prélèvement": "Prélèvement", traite: "Traite", especes: "Espèces" };
import PaymentsTab from "@/components/PaymentsTab";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { DocumentList } from "@/components/documents/DocumentList";

function formatEur(v) {
  return new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(v || 0);
}

const ALL_DOC_TYPES = [
  { value: "devis", label: "Devis", icon: FileText, workflows: ["devis_facture"] },
  { value: "proforma", label: "Devis", icon: FileText, workflows: ["proforma_commande_facture"] },
  { value: "commande", label: "Commande", icon: ShoppingCart, workflows: ["proforma_commande_facture"] },
  { value: "facture", label: "Facture", icon: Receipt, workflows: ["devis_facture", "proforma_commande_facture"] },
  { value: "avoir", label: "Avoir", icon: RotateCcw, workflows: ["devis_facture", "proforma_commande_facture"] },
];
const PROSPECT_DOC_TYPES = ["devis", "proforma"];


const DOC_TYPE_LABELS_MAP = { devis: "Devis", proforma: "Devis", facture: "Facture", commande: "Commande", avoir: "Avoir" };

function getLegalStatusStyle(status) {
  if (status === "En activité") return { bg: "bg-emerald-100 text-emerald-700", dot: "bg-emerald-500" };
  if (status?.startsWith("Fermee")) return { bg: "bg-red-100 text-red-700", dot: "bg-red-500" };
  return { bg: "bg-amber-100 text-amber-700", dot: "bg-amber-500" };
}

function EnrichButton({ client, onUpdated }) {
  const [loading, setLoading] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState({});
  const [applying, setApplying] = useState(false);

  const handleEnrich = async () => {
    setLoading(true);
    try {
      const r = await aiAPI.enrichClient(client.id);
      setData(r.data);
      const initial = {};
      (r.data.comparison || []).forEach(c => { if (c.changed) initial[c.key] = true; });
      setSelected(initial);
      setShowDialog(true);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur d'enrichissement"); }
    setLoading(false);
  };

  const handleApply = async () => {
    const fields = {};
    (data?.comparison || []).forEach(c => { if (selected[c.key]) fields[c.key] = c.suggested; });
    if (Object.keys(fields).length === 0) { toast.error("Sélectionnez au moins un champ"); return; }
    setApplying(true);
    try {
      const r = await aiAPI.applyEnrichment(client.id, fields);
      toast.success(`${Object.keys(fields).length} champ(s) mis à jour`);
      onUpdated?.(r.data);
      setShowDialog(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    setApplying(false);
  };

  const changedItems = useMemo(() => data?.comparison?.filter(c => c.changed) || [], [data]);
  const changedCount = changedItems.length;
  const selectedCount = useMemo(() => Object.values(selected).filter(Boolean).length, [selected]);

  return (
    <>
      <Button variant="outline" size="sm" className="h-8 px-3 text-xs border-amber-300 text-amber-700 hover:bg-amber-50" onClick={handleEnrich} disabled={loading} data-testid="enrich-client-btn">
        {loading ? <Loader2 size={13} className="sm:mr-1.5 animate-spin" /> : <Sparkles size={13} className="sm:mr-1.5" />}
        <span className="hidden sm:inline">{loading ? "Analyse..." : "Enrichir avec l'IA"}</span>
      </Button>
      <Dialog open={showDialog} onOpenChange={v => { if (!v) setShowDialog(false); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Sparkles size={18} className="text-amber-500" />Enrichissement IA</DialogTitle>
          </DialogHeader>
          {data && (
            <div className="space-y-4">
              {data.sirene_warning && <p className="text-xs text-amber-700 bg-amber-50 rounded-md p-2 border border-amber-200">{data.sirene_warning}</p>}
              {data.confidence_note && <p className="text-xs text-slate-500 bg-slate-50 rounded-md p-2 border">{data.confidence_note}</p>}
              {changedCount === 0 ? (
                <p className="text-sm text-slate-500 py-4 text-center">Aucune nouvelle information trouvee pour ce client.</p>
              ) : (
                <>
                  <p className="text-sm text-slate-500">{changedCount} champ(s) a enrichir :</p>
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50">
                          <TableHead className="w-10"></TableHead>
                          <TableHead className="text-xs">Champ</TableHead>
                          <TableHead className="text-xs">Actuel</TableHead>
                          <TableHead className="text-xs">Suggere</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {changedItems.map(c => (
                          <TableRow key={c.key} className={selected[c.key] ? "bg-amber-50/50" : ""}>
                            <TableCell>
                              <input type="checkbox" checked={!!selected[c.key]} onChange={e => setSelected(s => ({ ...s, [c.key]: e.target.checked }))} className="accent-amber-500" data-testid={`enrich-check-${c.key}`} />
                            </TableCell>
                            <TableCell className="text-xs font-medium text-slate-600">{c.label}</TableCell>
                            <TableCell className="text-xs text-slate-400">{c.current || <span className="italic">vide</span>}</TableCell>
                            <TableCell className="text-xs font-medium text-amber-700">{c.suggested}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </>
              )}
              <div className="flex justify-end gap-2 border-t pt-3">
                <Button variant="outline" size="sm" onClick={() => setShowDialog(false)}>Fermer</Button>
                {changedCount > 0 && (
                  <Button size="sm" className="bg-amber-600 hover:bg-amber-700 text-white" onClick={handleApply} disabled={applying || selectedCount === 0} data-testid="apply-enrichment-btn">
                    {applying ? <Loader2 size={13} className="mr-1.5 animate-spin" /> : <Check size={13} className="mr-1.5" />}
                    Appliquer ({selectedCount})
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

function InfoField({ label, value, mono, children, testId }) {
  return (
    <div className="flex items-start py-2 border-b border-slate-100 last:border-b-0" data-testid={testId}>
      <span className="text-[11px] text-slate-400 uppercase tracking-wider w-20 sm:w-28 flex-shrink-0 pt-0.5">{label}</span>
      {children || <span className={`text-sm text-slate-700 break-words min-w-0 ${mono ? "font-mono" : ""}`}>{value || <span className="text-slate-300 italic">-</span>}</span>}
    </div>
  );
}

function EditFieldInput({ type, value, onChange, placeholder, testId }) {
  if (type === "textarea") {
    return <textarea value={value} onChange={onChange} placeholder={placeholder} className="flex-1 h-16 text-sm border border-slate-200 rounded-md px-3 py-1.5 focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 outline-none resize-none" data-testid={testId} />;
  }
  if (type === "select") {
    return (
      <Select value={value || "none"} onValueChange={v => onChange({ target: { value: v === "none" ? "" : v } })}>
        <SelectTrigger className="flex-1 h-9 text-sm" data-testid={testId}><SelectValue placeholder="-" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="none">-</SelectItem>
          <SelectItem value="virement">Virement</SelectItem>
          <SelectItem value="cheque">Chèque</SelectItem>
          <SelectItem value="carte">Carte bancaire</SelectItem>
          <SelectItem value="prélèvement">Prélèvement</SelectItem>
          <SelectItem value="traite">Traite</SelectItem>
          <SelectItem value="especes">Espèces</SelectItem>
        </SelectContent>
      </Select>
    );
  }
  return <Input value={value} onChange={onChange} placeholder={placeholder} className="flex-1 h-9 text-sm" data-testid={testId} />;
}

function EditField({ label, value, onChange, type = "text", placeholder, testId }) {
  return (
    <div className="flex items-center gap-3 py-1.5">
      <label className="text-[11px] text-slate-400 uppercase tracking-wider w-28 flex-shrink-0">{label}</label>
      <EditFieldInput type={type} value={value} onChange={onChange} placeholder={placeholder} testId={testId} />
    </div>
  );
}


function getContactField(list, single, accessor) {
  if (list?.length > 0) {
    const first = list[0];
    return typeof first === 'object' ? first.value : first;
  }
  return single || "";
}

function ClientInfoTab({ client, onUpdated, onLoadAiSummary, aiLoading, aiSummary }) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});

  const startEdit = () => {
    const emailsList = client.emails?.length > 0
      ? client.emails.map(e => (typeof e === 'object' ? e.value : e))
      : [client.email || ""];
    const phonesList = client.phones?.length > 0
      ? client.phones.map(p => (typeof p === 'object' ? p.value : p))
      : [client.phone || ""];
    setForm({
      company: client.company || client.nom_etablissement || "",
      trade_name: client.trade_name || "",
      first_name: client.first_name || "",
      last_name: client.last_name || "",
      emails: emailsList,
      phones: phonesList,
      address: client.address || "",
      postal_code: client.postal_code || "",
      city: client.city || "",
      country: client.country || "",
      siret: client.siret || "",
      rci: client.rci || "",
      dsee: client.dsee || "",
      tva_number: client.tva_number || "",
      tva_intra: client.tva_intra || "",
      sector: client.sector || "",
      legal_form: client.legal_form || "",
      workforce: client.workforce || "",
      website: client.website || "",
      payment_method: client.payment_method || "",
      payment_delay: client.payment_delay || "",
      notes: client.notes || "",
      contact_name: client.contact_name || "",
      contact_email: client.contact_email || "",
    });
    setEditing(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        email: form.emails?.filter(Boolean)[0] || "",
        phone: form.phones?.filter(Boolean)[0] || "",
      };
      await clientAPI.update(client.id, payload);
      toast.success("Client mis à jour");
      onUpdated?.({ ...client, ...payload });
      setEditing(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  const updateEmail = (i, val) => { const e = [...form.emails]; e[i] = val; setForm(f => ({ ...f, emails: e })); };
  const addEmail = () => setForm(f => ({ ...f, emails: [...f.emails, ""] }));
  const removeEmail = (i) => { const e = form.emails.filter((_, idx) => idx !== i); setForm(f => ({ ...f, emails: e.length ? e : [""] })); };
  const updatePhone = (i, val) => { const p = [...form.phones]; p[i] = val; setForm(f => ({ ...f, phones: p })); };
  const addPhone = () => setForm(f => ({ ...f, phones: [...f.phones, ""] }));
  const removePhone = (i) => { const p = form.phones.filter((_, idx) => idx !== i); setForm(f => ({ ...f, phones: p.length ? p : [""] })); };

  const fld = (field) => ({
    value: form[field] || "",
    onChange: e => setForm(f => ({ ...f, [field]: e.target.value })),
  });

  if (editing) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wider">Modifier les informations</h3>
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" className="h-8 px-3 text-xs" onClick={() => setEditing(false)} data-testid="cancel-edit-client"><X size={13} className="mr-1" /> Annuler</Button>
            <Button size="sm" className="h-8 px-4 text-xs bg-indigo-600 hover:bg-indigo-700 text-white" onClick={save} disabled={saving} data-testid="save-client-btn">
              {saving ? <Loader2 size={13} className="mr-1 animate-spin" /> : <Save size={13} className="mr-1" />} Enregistrer
            </Button>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-0">
          <div>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 mt-2 border-b pb-1.5">Société & Contact</p>
            <EditField label="Raison sociale" {...fld("company")} placeholder="Nom de la société" testId="edit-company" />
            <EditField label="Nom commercial" {...fld("trade_name")} placeholder="Nom court / commercial" testId="edit-trade_name" />
            <EditField label="Prénom" {...fld("first_name")} placeholder="Prénom" testId="edit-first_name" />
            <EditField label="Nom" {...fld("last_name")} placeholder="Nom" testId="edit-last_name" />
            <EditField label="Contact" {...fld("contact_name")} placeholder="Nom du contact" testId="edit-contact_name" />
            <EditField label="Email contact" {...fld("contact_email")} placeholder="email@contact.com" testId="edit-contact_email" />
            <div className="py-1.5">
              <div className="flex items-center gap-3">
                <label className="text-[11px] text-slate-400 uppercase tracking-wider w-28 flex-shrink-0">Emails</label>
                <Button type="button" variant="ghost" size="sm" className="h-6 px-2 text-[11px] text-indigo-600" onClick={addEmail}><Plus size={11} className="mr-1" />Ajouter</Button>
              </div>
              {form.emails?.map((em, i) => (
                <div key={`email-${i}-${em.slice(0,8)}`} className="flex items-center gap-2 ml-[7.75rem] mt-1">
                  <Input value={em} onChange={e => updateEmail(i, e.target.value)} placeholder="email@société.com" className="flex-1 h-8 text-sm" data-testid={`edit-email-${i}`} />
                  {form.emails.length > 1 && <Button variant="ghost" size="icon" className="h-7 w-7 text-red-400 hover:text-red-600" onClick={() => removeEmail(i)}><Trash2 size={12} /></Button>}
                </div>
              ))}
            </div>
            <div className="py-1.5">
              <div className="flex items-center gap-3">
                <label className="text-[11px] text-slate-400 uppercase tracking-wider w-28 flex-shrink-0">Téléphones</label>
                <Button type="button" variant="ghost" size="sm" className="h-6 px-2 text-[11px] text-indigo-600" onClick={addPhone}><Plus size={11} className="mr-1" />Ajouter</Button>
              </div>
              {form.phones?.map((ph, i) => (
                <div key={`phone-${i}-${ph.slice(0,6)}`} className="flex items-center gap-2 ml-[7.75rem] mt-1">
                  <Input value={ph} onChange={e => updatePhone(i, e.target.value)} placeholder="01 02 03 04 05" className="flex-1 h-8 text-sm" data-testid={`edit-phone-${i}`} />
                  {form.phones.length > 1 && <Button variant="ghost" size="icon" className="h-7 w-7 text-red-400 hover:text-red-600" onClick={() => removePhone(i)}><Trash2 size={12} /></Button>}
                </div>
              ))}
            </div>
          </div>
          <div>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 mt-2 border-b pb-1.5">Adresse &amp; Légal</p>
            <EditField label="Adresse" {...fld("address")} placeholder="Rue..." testId="edit-address" />
            <EditField label="Code postal" {...fld("postal_code")} placeholder="06000" testId="edit-postal_code" />
            <EditField label="Ville" {...fld("city")} placeholder="Nice" testId="edit-city" />
            <EditField label="Pays" {...fld("country")} placeholder="France" testId="edit-country" />
            {legalIdFields(form.country).map(f => (
              <EditField key={f.field} label={f.label} {...fld(f.field)} placeholder={f.placeholder} testId={`edit-${f.field}`} />
            ))}
            <EditField label="N° TVA" {...fld("tva_number")} placeholder="FR12345678901" testId="edit-tva_number" />
            <EditField label="Forme jur." {...fld("legal_form")} placeholder="SARL, SAS, etc." testId="edit-legal_form" />
            <EditField label="Secteur" {...fld("sector")} placeholder="Secteur d'activité" testId="edit-sector" />
            <EditField label="Effectif" {...fld("workforce")} placeholder="10-50" testId="edit-workforce" />
            <EditField label="Site web" {...fld("website")} placeholder="https://www.example.com" testId="edit-website" />
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 mt-4 border-b pb-1.5">Règlement</p>
            <EditField label="Mode règl." {...fld("payment_method")} type="select" testId="edit-payment_method" />
            <EditField label="Délai règl." {...fld("payment_delay")} placeholder="30 jours" testId="edit-payment_delay" />
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 mt-4 border-b pb-1.5">Notes</p>
            <EditField label="Notes" {...fld("notes")} type="textarea" placeholder="Notes internes..." testId="edit-notes" />
          </div>
        </div>
      </div>
    );
  }

  const emails = client.emails?.length > 0 ? client.emails : [client.email || ""].filter(Boolean);
  const phones = client.phones?.length > 0 ? client.phones : [client.phone || ""].filter(Boolean);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-end gap-2 flex-wrap">
        <EnrichButton client={client} onUpdated={onUpdated} />
        <Button variant="outline" size="sm" className="h-8 px-3 text-xs border-purple-300 text-purple-700 hover:bg-purple-50" onClick={onLoadAiSummary} disabled={aiLoading} data-testid="ai-summary-btn">
          {aiLoading ? <Loader2 size={13} className="mr-1.5 animate-spin" /> : <Search size={13} className="mr-1.5" />}
          <span className="hidden sm:inline">Résumé IA</span><span className="sm:hidden">Résumé</span>
        </Button>
        <Button variant="outline" size="sm" className="h-8 px-3 text-xs border-indigo-300 text-indigo-600 hover:bg-indigo-50" onClick={startEdit} data-testid="edit-client-btn">
          <Pencil size={13} className="mr-1" />Modifier
        </Button>
      </div>
      {aiSummary && (
        <div className="p-3 bg-indigo-50 rounded-md border border-indigo-100">
          <p className="text-sm text-indigo-900 leading-relaxed">{aiSummary.summary}</p>
          {aiSummary.stats && (
            <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs text-indigo-700">
              <span>CA: {formatEur(aiSummary.stats.total_ca)}</span>
              <span>En attente: {formatEur(aiSummary.stats.pending)}</span>
              <span>{aiSummary.stats.invoices} facture(s)</span>
              <span>{aiSummary.stats.quotes} devis</span>
            </div>
          )}
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-0">
        <div className="min-w-0">
          <h3 className="text-xs font-semibold text-indigo-600 uppercase tracking-wider mb-3 pb-1.5 border-b-2 border-indigo-100">Coordonnées</h3>
          {emails.length > 0 && (
            <InfoField label="Email">
              <div className="flex flex-col gap-0.5 min-w-0">
                {emails.map((e, i) => <span key={typeof e === 'object' ? e.value : e} className="text-sm text-slate-700 break-all">{typeof e === 'object' ? e.value : e}</span>)}
              </div>
            </InfoField>
          )}
          {phones.length > 0 && (
            <InfoField label="Telephone">
              <div className="flex flex-col gap-0.5">
                {phones.map((p, i) => <span key={typeof p === 'object' ? p.value : p} className="text-sm text-slate-700">{typeof p === 'object' ? p.value : p}</span>)}
              </div>
            </InfoField>
          )}
          {client.contact_name && <InfoField label="Contact" value={client.contact_name} />}
          {client.contact_email && <InfoField label="Email contact" value={client.contact_email} />}
          <InfoField label="Adresse" value={[client.address, [client.postal_code, client.city].filter(Boolean).join(" "), client.country].filter(Boolean).join(", ")} />
        </div>
        <div className="min-w-0">
          <h3 className="text-xs font-semibold text-indigo-600 uppercase tracking-wider mb-3 pb-1.5 border-b-2 border-indigo-100">Informations légales</h3>
          {client.company_legal_status && (
            <div className="mb-3">
              {(() => {
                const style = getLegalStatusStyle(client.company_legal_status);
                return (
                  <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ${style.bg}`} data-testid="company-legal-status">
                    <span className={`w-1.5 h-1.5 rounded-full ${style.dot}`} />
                    {client.company_legal_status}
                  </span>
                );
              })()}
            </div>
          )}
          {legalIdFields(client.country).map(f => (
            <InfoField key={f.field} label={f.label} value={client[f.field]} mono testId={`info-${f.field}`} />
          ))}
          <InfoField label="N° TVA" value={client.tva_number || client.tva_intra} mono testId="info-tva" />
          <InfoField label="Forme jur." value={client.legal_form} />
          <InfoField label="Secteur" value={client.sector} />
          <InfoField label="Effectif" value={client.workforce} />
          {client.website && <InfoField label="Site web">
            <a href={client.website.startsWith("http") ? client.website : `https://${client.website}`} target="_blank" rel="noopener noreferrer" className="text-sm text-indigo-600 hover:underline break-all">{client.website}</a>
          </InfoField>}
          <h3 className="text-xs font-semibold text-indigo-600 uppercase tracking-wider mb-3 mt-4 pb-1.5 border-b-2 border-indigo-100">Règlement</h3>
          <InfoField label="Mode règl." value={PAYMENT_METHOD_LABELS[client.payment_method] || client.payment_method} />
          <InfoField label="Délai règl." value={client.payment_delay} />
        </div>
      </div>
      {client.notes && (
        <div>
          <h3 className="text-xs font-semibold text-indigo-600 uppercase tracking-wider mb-2 pb-1.5 border-b-2 border-indigo-100">Notes</h3>
          <p className="text-sm text-slate-600 whitespace-pre-line bg-slate-50 rounded-lg p-3">{client.notes}</p>
        </div>
      )}
    </div>
  );
}



function ProductContractsTable({ clientId }) {
  const pm = usePaymentMethods();
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showArchived, setShowArchived] = useState(false);

  const reload = useCallback(() => {
    productContractAPI.list(clientId, showArchived).then(r => { setContracts(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, [clientId, showArchived]);

  useEffect(() => { reload(); }, [reload]);

  const openNew = () => { setEditing(null); setShowDialog(true); };
  const openEdit = (c) => { setEditing(c); setShowDialog(true); };

  const archive = async (id) => {
    try {
      await productContractAPI.archive(id);
      toast.success("Contrat archivé — visible dans 'Voir archivés'");
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de l'archivage");
    }
  };

  const unarchive = async (id) => {
    try {
      await productContractAPI.unarchive(id);
      toast.success("Contrat restauré");
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de la restauration");
    }
  };

  if (loading) return <p className="text-sm text-slate-400 py-4 text-center">Chargement...</p>;

  const activeCount = contracts.filter(c => !c.archived).length;
  const archivedCount = contracts.filter(c => c.archived).length;

  return (
    <>
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <h2 className="text-base sm:text-lg font-semibold text-slate-800">Contrats produits</h2>
        <div className="flex items-center gap-2">
          <Button
            variant={showArchived ? "default" : "outline"}
            size="sm"
            onClick={() => setShowArchived(!showArchived)}
            className="text-xs"
            data-testid="toggle-archived-contracts"
          >
            <Archive size={12} className="mr-1.5" />
            {showArchived ? `Masquer archivés` : `Voir archivés${archivedCount ? ` (${archivedCount})` : ""}`}
          </Button>
          <Button onClick={openNew} className="bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold tracking-wider" data-testid="add-contract-btn">
            <Plus size={14} className="mr-1.5" /><span className="hidden sm:inline">Nouveau contrat</span><span className="sm:hidden">Nouveau</span>
          </Button>
        </div>
      </div>
      <Table>
        <TableHeader>
          <TableRow className="bg-slate-50">
            <TableHead className="uppercase text-xs text-slate-500">Produit</TableHead>
            <TableHead className="uppercase text-xs text-slate-500 hidden sm:table-cell">Lignes</TableHead>
            <TableHead className="uppercase text-xs text-slate-500">Prix client</TableHead>
            <TableHead className="uppercase text-xs text-slate-500 hidden md:table-cell">Prochaine fact.</TableHead>
            <TableHead className="uppercase text-xs text-slate-500 hidden lg:table-cell">Mode abo.</TableHead>
            <TableHead className="uppercase text-xs text-slate-500 hidden lg:table-cell">Règlement</TableHead>
            <TableHead className="uppercase text-xs text-slate-500 w-auto md:w-28">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {contracts.map(c => (
            <TableRow key={c.id} className={`table-row-hover ${c.archived ? "opacity-60 bg-slate-50/60" : ""}`} data-testid={`contract-row-${c.id}`}>
              <TableCell className="font-medium text-xs sm:text-sm">
                <div className="flex items-center gap-2">
                  <ContractSummaryCell contract={c} testid={`client-contract-${c.id}`} />
                  {c.archived && <Badge variant="outline" className="text-[9px] uppercase border-slate-300 text-slate-500">Archivé</Badge>}
                </div>
              </TableCell>
              <TableCell className="text-sm text-slate-500 hidden sm:table-cell">{c.items?.length || 1} ligne{(c.items?.length || 1) > 1 ? "s" : ""}</TableCell>
              <TableCell className="money-value text-xs sm:text-sm">{formatEur(c.client_price)}</TableCell>
              <TableCell className="text-sm hidden md:table-cell">{c.next_billing_date ? new Date(c.next_billing_date + "T00:00:00").toLocaleDateString("fr-FR") : "-"}</TableCell>
              <TableCell className="hidden lg:table-cell"><Badge variant="outline" className="text-xs">{c.subscription_mode || "-"}</Badge></TableCell>
              <TableCell className="text-sm text-slate-500 hidden lg:table-cell">{pm.label(c.payment_method_code || c.payment_mode) || "-"}</TableCell>
              <TableCell>
                <div className="hidden md:flex gap-0.5">
                  {!c.archived && (
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(c)} data-testid={`edit-contract-${c.id}`}><Pencil size={13} /></Button>
                  )}
                  {c.archived ? (
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-emerald-600" title="Restaurer le contrat" onClick={() => unarchive(c.id)} data-testid={`unarchive-contract-${c.id}`}><RotateCcw size={13} /></Button>
                  ) : (
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-amber-600" title="Archiver le contrat" onClick={() => archive(c.id)} data-testid={`archive-contract-${c.id}`}><Archive size={13} /></Button>
                  )}
                </div>
                <div className="md:hidden">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-7 w-7"><MoreHorizontal size={16} /></Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="min-w-[160px]">
                      {!c.archived && <DropdownMenuItem className="text-xs cursor-pointer" onClick={() => openEdit(c)}><Pencil size={12} className="mr-2" />Modifier</DropdownMenuItem>}
                      {c.archived ? (
                        <DropdownMenuItem className="text-xs cursor-pointer text-emerald-600" onClick={() => unarchive(c.id)}><RotateCcw size={12} className="mr-2" />Restaurer</DropdownMenuItem>
                      ) : (
                        <DropdownMenuItem className="text-xs cursor-pointer text-amber-600" onClick={() => archive(c.id)}><Archive size={12} className="mr-2" />Archiver</DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </TableCell>
            </TableRow>
          ))}
          {contracts.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-8">{showArchived ? "Aucun contrat" : "Aucun contrat actif"}</TableCell></TableRow>}
        </TableBody>
      </Table>
      <ContractForm
        open={showDialog}
        onOpenChange={setShowDialog}
        clientId={clientId}
        contractData={editing}
        onSaved={reload}
      />
    </>
  );
}

export default function ClientDetailPage({ user }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const hasContrats = (user?.modules || []).includes("contrats");
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeDocType, setActiveDocType] = useState("info");
  const [aiSummary, setAiSummary] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [workflow, setWorkflow] = useState("devis_facture");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const clientRes = await clientAPI.get(id);
      setClient(clientRes.data);
    } catch {
      navigate("/clients");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { adminAPI.getSettings().then(r => { setWorkflow(r.data?.workflow || "devis_facture"); }).catch(() => {}); }, []);

  const loadAiSummary = async () => {
    setAiLoading(true);
    try {
      const res = await aiAPI.clientSummary(id);
      setAiSummary(res.data);
    } catch { toast.error("Erreur IA"); }
    finally { setAiLoading(false); }
  };

  const isProspect = client?.type === "prospect";
  const DOC_TYPES = ALL_DOC_TYPES
    .filter(dt => dt.workflows.includes(workflow))
    .filter(dt => !isProspect || PROSPECT_DOC_TYPES.includes(dt.value));


  const handleConvert = async () => {
    if (!window.confirm("Convertir ce prospect en client ?")) return;
    try {
      await clientAPI.convert(id);
      toast.success("Prospect converti en client");
      load();
    } catch { toast.error("Erreur lors de la conversion"); }
  };


  if (loading || !client) {
    return <div className="page-container flex items-center justify-center min-h-[60vh]"><div className="animate-spin rounded-full h-8 w-8 border-2 border-indigo-600 border-t-transparent" /></div>;
  }

  return (
    <div className="page-container" data-testid="client-detail-page">
      <Button variant="ghost" onClick={() => navigate("/clients")} className="mb-4 text-slate-500" data-testid="back-to-clients-btn">
        <ArrowLeft size={16} className="mr-2" /> Retour aux clients
      </Button>
      {/* Header Card */}
      <Card className="p-4 sm:p-5 mb-4 border border-slate-200 rounded-lg bg-white">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <h1 className="text-xl sm:text-2xl font-bold text-slate-900 truncate" style={{ fontFamily: 'Outfit, sans-serif' }}>{client.trade_name || client.company || `${client.first_name} ${client.last_name}`}</h1>
            <Badge variant={client.type === "client" ? "default" : "secondary"} className={client.type === "client" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}>
              {client.type === "client" ? "Client" : "Prospect"}
            </Badge>
            {isProspect && (
              <Button size="sm" variant="outline" onClick={handleConvert} className="text-emerald-600 border-emerald-300 hover:bg-emerald-50" data-testid="convert-prospect-btn">
                <UserCheck size={14} className="mr-1.5" /> Convertir
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={() => {
              window.dispatchEvent(new CustomEvent("open-task-panel", { detail: {
                client_id: client.id,
                client_name: client.company || `${client.first_name || ""} ${client.last_name || ""}`.trim(),
              }}));
            }} className="text-violet-600 border-violet-300 hover:bg-violet-50" data-testid="client-create-task-btn" title="Créer une tâche">
              <CheckSquare size={14} className="mr-1.5" /><span className="hidden sm:inline">Tâche</span>
            </Button>
          </div>
          {client.trade_name && client.company && <p className="text-sm text-slate-400">{client.company}</p>}
          {client.nom_etablissement && !client.trade_name && <p className="text-sm text-slate-500">{client.nom_etablissement}</p>}
          {(client.company || client.trade_name) && (client.first_name || client.last_name) && <p className="text-sm text-slate-500">{client.first_name} {client.last_name}</p>}
          <div className="flex flex-col sm:flex-row sm:items-center sm:flex-wrap gap-y-0.5 gap-x-5 mt-2 text-sm text-slate-500">
            {(client.emails?.length > 0 || client.email) && <span className="flex items-center gap-1.5 truncate"><Mail size={13} className="shrink-0" /><span className="truncate">{getContactField(client.emails, client.email)}</span></span>}
            {(client.phones?.length > 0 || client.phone) && <span className="flex items-center gap-1.5"><Phone size={13} className="shrink-0" />{getContactField(client.phones, client.phone)}</span>}
            {client.city && <span className="flex items-center gap-1.5"><MapPin size={13} className="shrink-0" />{[client.postal_code, client.city].filter(Boolean).join(" ")}</span>}
            {client[legalIdField(client.country).field] && <span className="font-mono text-xs truncate">{legalIdField(client.country).label} : {client[legalIdField(client.country).field]}</span>}
          </div>
        </div>
      </Card>
      {/* Tabs Card */}
      <Card className="p-3 sm:p-4 border border-slate-200 rounded-lg bg-white overflow-hidden">
        <Tabs value={activeDocType} onValueChange={setActiveDocType}>
          <div className="mb-4">
            <TabsList className="flex w-full h-auto">
              <TabsTrigger value="info" data-testid="tab-info" className="flex-1 sm:flex-none" title="Info">
                <Info size={14} className="sm:mr-1" /><span className="hidden sm:inline">Info</span>
              </TabsTrigger>
              {DOC_TYPES.map(dt => (
                <TabsTrigger key={dt.value} value={dt.value} data-testid={`tab-${dt.value}`} className="flex-1 sm:flex-none" title={dt.label}>
                  <dt.icon size={14} className="sm:mr-1" /><span className="hidden sm:inline">{dt.label}</span>
                </TabsTrigger>
              ))}
              {hasContrats && <TabsTrigger value="product_contracts" data-testid="tab-product-contracts" className="flex-1 sm:flex-none" title="Contrats">
                <PackageCheck size={14} className="sm:mr-1" /><span className="hidden sm:inline">Contrats</span>
              </TabsTrigger>}
              <TabsTrigger value="reglements" data-testid="tab-reglements" className="flex-1 sm:flex-none" title="Règlements">
                <CreditCard size={14} className="sm:mr-1" /><span className="hidden sm:inline">Règlements</span>
              </TabsTrigger>
            </TabsList>
          </div>
          <TabsContent value="info">
            <ClientInfoTab client={client} onUpdated={(updated) => setClient(updated)} onLoadAiSummary={loadAiSummary} aiLoading={aiLoading} aiSummary={aiSummary} />
          </TabsContent>
          {DOC_TYPES.map(dt => (
            <TabsContent key={dt.value} value={dt.value}>
              {activeDocType === dt.value && <DocumentList user={user} clientId={id} fixedType={dt.value} clientRecord={client} />}
            </TabsContent>
          ))}
          {hasContrats && <TabsContent value="product_contracts">
            <ProductContractsTable clientId={id} />
          </TabsContent>}
          <TabsContent value="reglements">
            <PaymentsTab clientId={id} clientName={client?.company || client?.nom_etablissement || ""} />
          </TabsContent>
        </Tabs>
      </Card>
    </div>
  );
}
